import React, { useState, useEffect } from 'react';
import { Amenity } from '../types/amenity';
import { getImages } from '../services/uploadService';
import { Pencil, Trash2 } from 'lucide-react';
import '../styles/AmenityViewModal.css';

interface AmenityViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  amenity: Amenity | null;
  workspaceId: string;
  onEdit?: (amenity: Amenity) => void;
  onDelete?: (amenity: Amenity) => void;
}

interface ImageData {
  _id: string;
  url: string;
}

const AmenityViewModal: React.FC<AmenityViewModalProps> = ({
  isOpen,
  onClose,
  amenity,
  workspaceId,
  onEdit,
  onDelete
}) => {
  const [images, setImages] = useState<ImageData[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loadingImages, setLoadingImages] = useState(false);

  useEffect(() => {
    if (isOpen && amenity) {
      fetchImages();
      setCurrentImageIndex(0);
    }
  }, [isOpen, amenity]);

  const fetchImages = async () => {
    if (!amenity) return;

    try {
      setLoadingImages(true);
      const fetchedImages = await getImages(workspaceId, {
        entityType: 'amenity',
        entityId: amenity._id,
        imageType: 'amenity'
      });
      setImages(fetchedImages);
    } catch (err) {
      console.error('Error fetching images:', err);
    } finally {
      setLoadingImages(false);
    }
  };

  if (!isOpen || !amenity) return null;

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="amenity-view-overlay" onClick={handleBackdropClick}>
      <div className="amenity-view-modal-vertical" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="amenity-view-close" onClick={onClose}>
          ✕
        </button>

        {/* Action Buttons */}
        {(onEdit || onDelete) && (
          <div className="amenity-view-actions">
            {onEdit && (
              <button
                className="amenity-view-action-btn amenity-edit-btn"
                onClick={() => {
                  onEdit(amenity);
                  onClose();
                }}
                title="Edit Amenity"
              >
                <Pencil size={20} />
              </button>
            )}
            {onDelete && (
              <button
                className="amenity-view-action-btn amenity-delete-btn"
                onClick={() => {
                  onDelete(amenity);
                  onClose();
                }}
                title="Delete Amenity"
              >
                <Trash2 size={20} />
              </button>
            )}
          </div>
        )}

        {/* Image Gallery Section */}
        <div className="amenity-view-gallery">
          {images.length > 0 ? (
            <>
              <img
                src={images[currentImageIndex]?.url}
                alt={amenity.title}
                className="amenity-view-image"
              />

              {images.length > 1 && (
                <>
                  {/* Navigation Arrows */}
                  <button
                    className="amenity-view-arrow amenity-view-prev"
                    onClick={handlePrevImage}
                  >
                    ‹
                  </button>
                  <button
                    className="amenity-view-arrow amenity-view-next"
                    onClick={handleNextImage}
                  >
                    ›
                  </button>

                  {/* Image Counter */}
                  <div className="amenity-view-counter">
                    {currentImageIndex + 1}/{images.length}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="amenity-view-no-image">
              <p>No images available</p>
            </div>
          )}
        </div>

        {/* Content Section - Now Below Images */}
        <div className="amenity-view-content">
          {/* Category Badge */}
          <div className="amenity-view-category">
            {amenity.category}
          </div>

          {/* Title */}
          <h2 className="amenity-view-title">{amenity.title}</h2>

          {/* Description */}
          {amenity.description && (
            <div
              className="amenity-view-description"
              dangerouslySetInnerHTML={{ __html: amenity.description }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AmenityViewModal;
